import { AnimatePresence, motion } from 'framer-motion';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';

import type { Title } from '@shared/types';

import { Poster } from '@/components/Poster';
import { Button } from '@/components/ui/button';
import { useTitleRating } from '@/features/show/api';
import { Stars } from '@/features/show/Stars';
import { localized } from '@/i18n/localized';
import { useLocale } from '@/i18n/locale';
import { sheetVariants } from '@/lib/motion';

/** Untouched prompts leave on their own; hover or keyboard focus pauses it. */
export const RATING_PROMPT_DISMISS_MS = 12_000;

interface RatingPromptValue {
  /** Ask the viewer to rate `title` — a no-op when it is rated or was dismissed this session. */
  prompt: (title: Title) => void;
}

const RatingPromptContext = createContext<RatingPromptValue | null>(null);

/**
 * The provider's `prompt`, or null outside it (tests, a screen rendered
 * alone) — the show mutation hooks then skip the prompt and its fetch.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useRatingPrompt(): RatingPromptValue | null {
  return useContext(RatingPromptContext);
}

const promptKey = (title: Pick<Title, 'id' | 'type'>) => `${title.type}:${title.id}`;

/**
 * The post-watch rating prompt (ui-ux.md §5, §7): mounted once in `AppShell`.
 * The show mutation hooks call `prompt(title)` when a TITLE becomes watched
 * (film marked watched, series completed / finished by its last episode) and
 * the viewer has no rating. One prompt at a time — the latest wins; a title
 * closed once (rated, "Not now", Esc, timeout) is not prompted again while
 * the provider lives (this session).
 */
export function RatingPromptProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<Title | null>(null);
  const closed = useRef(new Set<string>());

  const prompt = useCallback((title: Title) => {
    if (title.userRating || closed.current.has(promptKey(title))) return;
    setCurrent(title);
  }, []);

  const close = useCallback((key: string) => {
    closed.current.add(key);
    setCurrent((c) => (c && promptKey(c) === key ? null : c));
  }, []);

  const value = useMemo(() => ({ prompt }), [prompt]);

  return (
    <RatingPromptContext.Provider value={value}>
      {children}
      <AnimatePresence>
        {current && <PromptCard key={promptKey(current)} title={current} onClose={close} />}
      </AnimatePresence>
    </RatingPromptContext.Provider>
  );
}

function isFocusVisible(el: EventTarget | null): boolean {
  try {
    return el instanceof Element && el.matches(':focus-visible');
  } catch {
    return false;
  }
}

/**
 * Non-modal, docked bottom-centre: the page stays usable behind it. Focus
 * moves to the stars on open and returns to where it was on close.
 */
function PromptCard({ title, onClose }: { title: Title; onClose: (key: string) => void }) {
  const { locale, t } = useLocale();
  const name = localized(title.title, title.titleBr, locale);
  const rate = useTitleRating(title.id, title.type);
  const headingId = useId();
  const cardRef = useRef<HTMLDivElement | null>(null);
  // Whatever had focus when the prompt appeared (usually the action that finished the title).
  const [returnTo] = useState(() => document.activeElement);
  const [hovered, setHovered] = useState(false);
  const [keyboardFocus, setKeyboardFocus] = useState(false);

  const dismiss = useCallback(() => {
    if (
      cardRef.current?.contains(document.activeElement) &&
      returnTo instanceof HTMLElement &&
      returnTo.isConnected
    ) {
      returnTo.focus();
    }
    onClose(promptKey(title));
  }, [onClose, returnTo, title]);

  // Focus moves in on open, so focus alone doesn't mean "engaged": a mouse
  // user who looked away still gets the timeout; keyboard focus (focus-visible)
  // and hover pause it (WCAG 2.2.1). ponytail: a pause restarts the full 12 s.
  useEffect(() => {
    if (hovered || keyboardFocus) return;
    const timer = window.setTimeout(dismiss, RATING_PROMPT_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [hovered, keyboardFocus, dismiss]);

  const choose = (value: number | null) => {
    if (value === null) return;
    rate.mutate(value);
    toast.success(
      t(value === 1 ? 'ratingPrompt.rated_singular' : 'ratingPrompt.rated', { count: value }),
      { action: { label: t('ratingPrompt.undo'), onClick: () => rate.mutate(null) } },
    );
    dismiss();
  };

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(6rem+env(safe-area-inset-bottom))] z-40 flex justify-center px-4 md:bottom-6">
      <motion.div
        ref={cardRef}
        role="dialog"
        aria-modal="false"
        aria-labelledby={headingId}
        variants={sheetVariants()}
        initial="initial"
        animate="animate"
        exit="exit"
        onPointerEnter={() => setHovered(true)}
        onPointerLeave={() => setHovered(false)}
        onFocus={(e) => setKeyboardFocus(isFocusVisible(e.target))}
        onBlur={(e) => {
          if (!cardRef.current?.contains(e.relatedTarget as Node | null)) setKeyboardFocus(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            dismiss();
          }
        }}
        className="pointer-events-auto flex w-full max-w-[28rem] items-center gap-4 rounded-lg bg-surface p-4 shadow-sm"
      >
        <Poster
          id={title.id}
          name={name}
          palette={title.palette}
          imageUrl={title.posterUrl}
          imageFallbackUrl={title.posterFallbackUrl}
          layout={false}
          fallbackTitle={false}
          sizes="48px"
          className="w-12 shrink-0"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <p id={headingId} className="text-headline text-ink">
            {t('ratingPrompt.title', { title: name })}
          </p>
          <Stars
            value={null}
            onRate={choose}
            size="lg"
            selectOnMove={false}
            autoFocus
            aria-label={t('ratingPrompt.starsA11y', { title: name })}
          />
        </div>
        <Button variant="ghost" size="sm" onClick={dismiss} className="shrink-0 self-start">
          {t('ratingPrompt.notNow')}
        </Button>
      </motion.div>
    </div>
  );
}
